import { useMemo } from 'react';
import { Outlet, useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  CalendarDaysIcon,
  ChartBarIcon,
  ShoppingBagIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';

import Navbar, { Breadcrumb } from '../components/Navbar/Navbar';
import Sidebar from '../components/Sidebar/Sidebar';
import AssetsService from '../services/AssetsService';
import useStation from '../hooks/useStation';
import useLocationRole from '../hooks/useLocationRole';

const StationLayout = () => {
  const { siteId, stationId } = useParams<{ siteId: string; stationId: string }>();
  const { pathname } = useLocation();

  const { location, station } = useStation(siteId, stationId);

  const { data: locations } = useQuery({
    queryKey: ['locations'],
    queryFn: () => AssetsService.getLocations(),
    select: (data) => data.locations,
  });

  // Per-location: a teammate can have rentals on one site and not on another,
  // so the global flag would show these tabs on both sites or on neither.
  // `canManage` is per-site too, so !canManage means "teammate on this site".
  const { rentalsEnabled: rentalEnabled, canManage } = useLocationRole(siteId);

  const basePath = `/site/${siteId}/station/${stationId}`;
  const isSettings = pathname.endsWith('/settings');
  const isRentals = pathname.endsWith('/rentals');
  const isEquipments = pathname.endsWith('/equipments');
  const isOperations = pathname.endsWith('/operations');
  const isMonitoring = pathname.endsWith('/monitoring');
  const isOverview = pathname === basePath;

  const currentPageLabel = isSettings
    ? 'Paramètres'
    : isRentals
      ? 'Réservations'
      : isEquipments
        ? 'Équipements'
        : isOperations
          ? 'Opérations'
          : isMonitoring
            ? 'Monitoring'
            : 'Vue d\'ensemble';

  const stationName = station?.name || `Station ${station?.id}`;

  const siteDropdownOptions = useMemo(() => {
    return locations?.map((loc) => ({
      label: loc.name,
      href: `/site/${loc.id}`,
    })) ?? [];
  }, [locations]);

  const stationDropdownOptions = useMemo(() => {
    return location?.stations?.map((s) => ({
      label: s.name || `Station ${s.id}`,
      href: `/site/${siteId}/station/${s.id}`,
    })) ?? [];
  }, [location?.stations, siteId]);

  const breadcrumbs = useMemo<Breadcrumb[]>(() => {
    const hasMultipleSites = siteDropdownOptions.length > 1;
    const hasMultipleStations = stationDropdownOptions.length > 1;
    const crumbs: Breadcrumb[] = [
      {
        label: location?.name ?? '...',
        href: `/site/${siteId}`,
        icon: <img src="/site.svg" alt="" className="w-3.5 h-3.5 brightness-0 invert" />,
        ...(hasMultipleSites && { dropdownTitle: 'Sites', dropdownOptions: siteDropdownOptions }),
      },
      {
        label: stationName,
        href: basePath,
        icon: <img src="/station.svg" alt="" className="w-3.5 h-3.5 brightness-0 invert" />,
        ...(hasMultipleStations && { dropdownTitle: 'Stations', dropdownOptions: stationDropdownOptions }),
      },
    ];

    if (!isOverview) {
      crumbs.push({ label: currentPageLabel });
    }

    return crumbs;
  }, [location?.name, siteId, stationName, basePath, currentPageLabel, isOverview, siteDropdownOptions, stationDropdownOptions]);

  const sidebarItems = useMemo(() => {
    const items = [
      // The overview is built from /v1/subscriptions/usage and /lock-usage,
      // which are owner/admin-only — a teammate would just see an empty page.
      ...(canManage ? [{
        icon: <img src="/station.svg" alt="" className="w-5 h-5 brightness-0 invert opacity-80" />,
        label: 'Vue d\'ensemble',
        href: basePath,
        active: isOverview,
      }] : []),
      {
        icon: <WrenchScrewdriverIcon className="w-5 h-5 text-white-alpha-80" />,
        label: 'Opérations',
        href: `${basePath}/operations`,
        active: isOperations,
      },
      {
        icon: <ChartBarIcon className="w-5 h-5 text-white-alpha-80" />,
        label: 'Monitoring',
        href: `${basePath}/monitoring`,
        active: isMonitoring,
      },
      ...(rentalEnabled ? [
        {
          icon: <CalendarDaysIcon className="w-5 h-5 text-white-alpha-80" />,
          label: 'Réservations',
          href: `${basePath}/rentals`,
          active: isRentals,
        },
        {
          icon: <ShoppingBagIcon className="w-5 h-5 text-white-alpha-80" />,
          label: 'Équipements',
          href: `${basePath}/equipments`,
          active: isEquipments,
        },
      ] : []),
      // Everything here is owner-only: renaming, activation, the danger zone.
      // A teammate would get a disabled name field and the station token.
      ...(canManage ? [{
        icon: <img src="/settings.svg" alt="" className="w-5 h-5" />,
        label: 'Paramètres',
        href: `${basePath}/settings`,
        active: isSettings,
      }] : []),
    ];
    return items;
  }, [basePath, isOverview, isOperations, isMonitoring, isSettings, isRentals, isEquipments, rentalEnabled, canManage]);

  return (
    <div className="flex flex-col h-screen bg-off-white">
      <Navbar
        breadcrumbs={breadcrumbs}
        sidebarItems={<Sidebar items={sidebarItems} isMobile />}
      />
      <div className="self-stretch flex-1 inline-flex justify-center items-start overflow-hidden">
        <Sidebar items={sidebarItems} isMobile={false} />
        <div className="flex-1 self-stretch pt-16 inline-flex flex-col justify-start items-center gap-8 overflow-auto">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default StationLayout;
